import {
  durationAcrossMidnight,
  InvalidTimeFormatError,
  roundUpQuarter,
} from '@avity/engine'
import { Sun, Moon } from 'lucide-react'
import { useId } from 'react'

/**
 * Two-period time slot editor (matin / après-midi).
 *
 * UX background
 * -------------
 * The V1 form had two separate `<input type="time">` pairs that
 * the chauffeur typed into. That worked, but had two papercuts
 * we fix here:
 *
 *   1. **No live feedback.** Drivers couldn't tell at a glance how
 *      many hours they'd actually clocked until they hit submit.
 *      We now display the per-slot duration ("4h00") in the row
 *      header and the rounded-up version in muted text next to
 *      each input, so the chauffeur can verify both the raw and
 *      billed values.
 *   2. **Quarter-hour rounding was opaque.** The backend rounds
 *      every input up to the next quarter for billing reasons. The
 *      driver was confused when the printed bon showed 08:15 after
 *      they typed 08:07. We now show the rounded value inline as
 *      "→ 08:15" so they understand where the difference comes
 *      from.
 *
 * Implementation notes
 * --------------------
 * - We expose the slots as a 4-tuple `[m_start, m_end, a_start,
 *   a_end]` of strings to match the shape `<input type="time">`
 *   gives us. An empty string means "not filled in"; we don't use
 *   `null` here because it would force two state shapes for the
 *   same control. Conversion to the engine's `TimeSlot[]` happens
 *   at the parent (form) level when computing totals or
 *   submitting.
 * - `roundUpQuarter` throws `InvalidTimeFormatError` on garbage
 *   input. We catch it and silently render no rounded indicator
 *   rather than crash — the input is still a half-typed value
 *   the user is editing.
 */

export type TimeQuad = [string, string, string, string]

interface TimeSlotsProps {
  value: TimeQuad
  onChange: (next: TimeQuad) => void
  /** Override per-slot error labels — same length as the quad. */
  errors?: [string?, string?, string?, string?]
}

export function TimeSlots({ value, onChange, errors }: TimeSlotsProps) {
  const morningHeadingId = useId()
  const afternoonHeadingId = useId()

  function updateAt(index: 0 | 1 | 2 | 3, next: string): void {
    const copy: TimeQuad = [...value] as TimeQuad
    copy[index] = next
    onChange(copy)
  }

  return (
    <div className="flex flex-col gap-4">
      <SlotRow
        labelId={morningHeadingId}
        title="Matin"
        icon={<Sun size={16} aria-hidden="true" />}
        startValue={value[0]}
        endValue={value[1]}
        startError={errors?.[0]}
        endError={errors?.[1]}
        onStartChange={(v) => updateAt(0, v)}
        onEndChange={(v) => updateAt(1, v)}
      />
      <SlotRow
        labelId={afternoonHeadingId}
        title="Après-midi / soir"
        icon={<Moon size={16} aria-hidden="true" />}
        startValue={value[2]}
        endValue={value[3]}
        startError={errors?.[2]}
        endError={errors?.[3]}
        onStartChange={(v) => updateAt(2, v)}
        onEndChange={(v) => updateAt(3, v)}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  One row (start + end)                                                      */
/* -------------------------------------------------------------------------- */

interface SlotRowProps {
  labelId: string
  title: string
  icon: React.ReactNode
  startValue: string
  endValue: string
  startError?: string | undefined
  endError?: string | undefined
  onStartChange: (next: string) => void
  onEndChange: (next: string) => void
}

function SlotRow({
  labelId,
  title,
  icon,
  startValue,
  endValue,
  startError,
  endError,
  onStartChange,
  onEndChange,
}: SlotRowProps) {
  const duration = computeDurationLabel(startValue, endValue)

  return (
    <fieldset className="border border-surface-border rounded-xl p-3 bg-surface">
      <legend
        id={labelId}
        className="px-2 text-sm font-semibold text-text-muted uppercase tracking-wide flex items-center gap-2"
      >
        {icon}
        {title}
        {duration !== null ? (
          <span className="ml-auto text-brand font-mono normal-case">
            {duration}
          </span>
        ) : null}
      </legend>
      <div className="flex items-end gap-3 mt-2">
        <TimeInput
          label="Début"
          value={startValue}
          onChange={onStartChange}
          error={startError}
        />
        <span
          aria-hidden="true"
          className="text-text-muted text-xl pb-3 select-none"
        >
          →
        </span>
        <TimeInput
          label="Fin"
          value={endValue}
          onChange={onEndChange}
          error={endError}
        />
      </div>
    </fieldset>
  )
}

/* -------------------------------------------------------------------------- */
/*  Single time input + rounded indicator                                      */
/* -------------------------------------------------------------------------- */

interface TimeInputProps {
  label: string
  value: string
  onChange: (next: string) => void
  error?: string | undefined
}

function TimeInput({ label, value, onChange, error }: TimeInputProps) {
  const inputId = useId()
  const rounded = computeRoundedLabel(value)
  const showRoundedHint =
    rounded !== null && rounded !== '' && stripSeconds(rounded) !== value

  return (
    <div className="flex-1 flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs text-text-muted">
        {label}
      </label>
      <input
        id={inputId}
        type="time"
        // The 5-min step keeps the iOS picker comfortable. We let
        // the user type other values (07:08) freely; the backend
        // rounds up.
        step={60}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={[
          'min-h-[3rem] w-full rounded-xl border-2 px-3',
          'text-base bg-surface text-text',
          'focus:outline-none',
          error !== undefined && error !== ''
            ? 'border-danger focus:border-danger'
            : 'border-surface-border focus:border-brand',
        ].join(' ')}
      />
      {/* Rounded-up indicator. Reserves space when absent so the
          row doesn't jump as the user types. */}
      <p className="min-h-[1rem] text-xs text-text-muted">
        {showRoundedHint ? `→ ${stripSeconds(rounded ?? '')} (arrondi)` : ''}
      </p>
      {error !== undefined && error !== '' ? (
        <p className="text-xs text-danger">{error}</p>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Helpers — engine-backed but tolerant of partial input                      */
/* -------------------------------------------------------------------------- */

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/u

/**
 * "4h00" / "1h45" / "—" depending on whether both ends are valid
 * and produce a non-zero duration. Crossing midnight is supported
 * (handled by `durationAcrossMidnight`).
 */
function computeDurationLabel(start: string, end: string): string | null {
  if (!HH_MM.test(start) || !HH_MM.test(end)) return null
  const minutes = durationAcrossMidnight(start, end)
  if (minutes === 0) return null
  return formatMinutes(minutes)
}

/**
 * Returns the rounded-up quarter as `HH:MM:SS` or `null` if the
 * input isn't a valid time yet (the user is mid-typing).
 */
function computeRoundedLabel(value: string): string | null {
  if (!HH_MM.test(value)) return null
  try {
    return roundUpQuarter(value)
  } catch (err) {
    if (err instanceof InvalidTimeFormatError) return null
    throw err
  }
}

function stripSeconds(value: string): string {
  // engine returns "HH:MM:SS"; the input shows "HH:MM" so we
  // trim before comparing.
  return value.length === 8 ? value.slice(0, 5) : value
}

function formatMinutes(total: number): string {
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  return `${hours}h${String(minutes).padStart(2, '0')}`
}
